import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import {
  Plus,
  Pencil,
  Trash2,
  Star,
  RefreshCw,
  Search,
  Image as ImageIcon,
  Save,
  X,
  Package,
  Eye,
  EyeOff,
  TrendingUp,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent } from '../ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORY_META = {
  low: { label: 'Low Margin', plan: 'Basic Plan', color: 'from-blue-500 to-blue-600', badge: 'bg-blue-100 text-blue-700 border-blue-300' },
  medium: { label: 'Medium Margin', plan: 'Advanced Plan', color: 'from-indigo-500 to-indigo-600', badge: 'bg-indigo-100 text-indigo-700 border-indigo-300' },
  high: { label: 'High Margin', plan: 'Professional Plan', color: 'from-purple-500 to-purple-600', badge: 'bg-purple-100 text-purple-700 border-purple-300' },
};

const emptyProduct = {
  name: '',
  image: '',
  productCost: 0,
  sellingPrice: 0,
  profit: 0,
  profitMargin: 0,
  avgCPP: '',
  ordersOn1000: '',
  ordersOn2000: '',
  ordersOn3000: '',
  category: 'low',
  description: '',
  recommended: false,
  active: true,
  sortOrder: 0,
};

const ProductsCatalogManagement = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeCategory, setActiveCategory] = useState('low');
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyProduct);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`${API}/admin/products`);
      setProducts(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    const byCat = products.filter((p) => p.category === activeCategory);
    if (!search) return byCat;
    const s = search.toLowerCase();
    return byCat.filter(
      (p) =>
        (p.name || '').toLowerCase().includes(s) ||
        (p.description || '').toLowerCase().includes(s)
    );
  }, [products, activeCategory, search]);

  const counts = useMemo(() => ({
    low: products.filter((p) => p.category === 'low').length,
    medium: products.filter((p) => p.category === 'medium').length,
    high: products.filter((p) => p.category === 'high').length,
  }), [products]);

  const openAdd = () => {
    setEditing('new');
    setForm({ ...emptyProduct, category: activeCategory, sortOrder: filtered.length });
  };

  const openEdit = (p) => {
    setEditing(p.id);
    setForm({
      ...emptyProduct,
      ...p,
      productCost: Number(p.productCost) || 0,
      sellingPrice: Number(p.sellingPrice) || 0,
      profit: Number(p.profit) || 0,
      profitMargin: Number(p.profitMargin) || 0,
    });
  };

  const closeEdit = () => {
    setEditing(null);
    setForm(emptyProduct);
  };

  const handleChange = (key, value) => {
    setForm((prev) => {
      const next = { ...prev, [key]: value };
      // Auto-calc profit and margin when cost/sell changes
      if (key === 'productCost' || key === 'sellingPrice') {
        const cost = Number(next.productCost) || 0;
        const sell = Number(next.sellingPrice) || 0;
        const profit = Math.max(0, sell - cost);
        next.profit = profit;
        next.profitMargin = sell > 0 ? Math.round((profit / sell) * 1000) / 10 : 0;
      }
      return next;
    });
  };

  const handleSave = async () => {
    if (!form.name || !form.image || !form.category) {
      alert('Please fill name, image URL and category');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        productCost: Number(form.productCost) || 0,
        sellingPrice: Number(form.sellingPrice) || 0,
        profit: Number(form.profit) || 0,
        profitMargin: Number(form.profitMargin) || 0,
        sortOrder: Number(form.sortOrder) || 0,
      };
      if (editing === 'new') {
        await axios.post(`${API}/admin/products`, payload);
      } else {
        await axios.put(`${API}/admin/products/${editing}`, payload);
      }
      await load();
      closeEdit();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to save product');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (p) => {
    if (!window.confirm(`Delete "${p.name}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/products/${p.id}`);
      await load();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const toggleActive = async (p) => {
    try {
      await axios.put(`${API}/admin/products/${p.id}`, {
        ...p,
        active: !p.active,
      });
      await load();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to toggle');
    }
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold text-slate-800">Onboarding Product Cards</h2>
          <p className="text-sm text-slate-500">
            These products are shown to users during the onboarding "Choose Your Winning Products" step.
            Basic → Low Margin · Advanced → Medium · Professional → High Margin.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={load} variant="outline" size="sm" disabled={loading} className="border-slate-200">
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button onClick={openAdd} className="bg-blue-600 hover:bg-blue-700 text-white shadow-md shadow-blue-500/30">
            <Plus className="w-4 h-4 mr-1" /> Add Product
          </Button>
        </div>
      </div>

      {/* Category Tabs */}
      <div className="grid grid-cols-3 gap-2 sm:gap-3">
        {Object.entries(CATEGORY_META).map(([key, meta]) => (
          <button
            key={key}
            onClick={() => setActiveCategory(key)}
            className={`min-w-0 p-2.5 sm:p-4 rounded-xl border-2 transition-all text-left ${
              activeCategory === key
                ? `bg-gradient-to-br ${meta.color} text-white border-transparent shadow-lg`
                : 'bg-white border-slate-200 hover:border-blue-300 text-slate-700'
            }`}
          >
            <div className="text-[10px] sm:text-xs uppercase tracking-wide opacity-80 truncate">{meta.plan}</div>
            <div className="text-sm sm:text-lg font-bold leading-tight">{meta.label}</div>
            <div className={`text-xs sm:text-sm mt-1 ${activeCategory === key ? 'text-white/90' : 'text-slate-500'}`}>
              {counts[key]} product{counts[key] === 1 ? '' : 's'}
            </div>
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name or description..."
          className="pl-9 bg-white border-slate-200"
        />
      </div>

      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
          {error}
        </div>
      )}

      {/* Product Grid */}
      {loading ? (
        <div className="text-center py-10 text-slate-500">Loading...</div>
      ) : filtered.length === 0 ? (
        <Card className="bg-white border-slate-200">
          <CardContent className="py-12 text-center text-slate-500">
            <Package className="w-12 h-12 mx-auto mb-3 text-slate-300" />
            <p className="font-medium">No products in this category yet</p>
            <p className="text-sm mt-1">Click "Add Product" to create the first one.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filtered.map((p) => (
            <Card key={p.id} className={`bg-white border-slate-200 hover:border-blue-300 transition-all overflow-hidden ${!p.active ? 'opacity-60' : ''}`}>
              <div className="aspect-square relative bg-slate-50">
                <img
                  src={p.image}
                  alt={p.name}
                  className="w-full h-full object-contain p-3"
                  onError={(e) => {
                    e.target.src = 'https://via.placeholder.com/300x300?text=' + encodeURIComponent(p.name);
                  }}
                />
                {p.recommended && (
                  <div className="absolute top-2 left-2 px-2 py-1 bg-gradient-to-r from-yellow-400 to-orange-400 rounded-full flex items-center gap-1 shadow-md">
                    <Star className="w-3 h-3 text-white" fill="white" />
                    <span className="text-[10px] text-white font-bold">RECOMMENDED</span>
                  </div>
                )}
                {!p.active && (
                  <div className="absolute top-2 right-2 px-2 py-1 bg-slate-700 rounded-full">
                    <span className="text-[10px] text-white font-bold">HIDDEN</span>
                  </div>
                )}
              </div>
              <CardContent className="p-3">
                <div className="font-semibold text-slate-800 text-sm line-clamp-2 mb-2 min-h-[2.5rem]">{p.name}</div>
                <div className="grid grid-cols-3 gap-1 text-[11px] mb-2">
                  <div className="text-slate-500">Cost<br /><span className="text-slate-800 font-semibold">₹{p.productCost}</span></div>
                  <div className="text-slate-500">Sell<br /><span className="text-slate-800 font-semibold">₹{p.sellingPrice}</span></div>
                  <div className="text-slate-500">Profit<br /><span className="text-green-600 font-semibold">₹{p.profit}</span></div>
                </div>
                <div className="flex items-center justify-between text-[11px] text-slate-500 pb-2 border-b border-slate-100 mb-2">
                  <span className="flex items-center gap-1"><TrendingUp className="w-3 h-3 text-green-600" />{Number(p.profitMargin || 0).toFixed(1)}%</span>
                  <span className="text-blue-600">CPP: ₹{p.avgCPP}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Button size="sm" variant="outline" onClick={() => openEdit(p)} className="flex-1 border-slate-200 h-8 text-xs">
                    <Pencil className="w-3 h-3 mr-1" /> Edit
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => toggleActive(p)} className="h-8 w-8 p-0" title={p.active ? 'Hide from users' : 'Show to users'}>
                    {p.active ? <Eye className="w-4 h-4 text-slate-500" /> : <EyeOff className="w-4 h-4 text-amber-600" />}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => handleDelete(p)} className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && closeEdit()}>
        <DialogContent className="bg-white text-slate-800 border border-slate-200 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl text-blue-600 flex items-center gap-2">
              <Package className="w-5 h-5" />
              {editing === 'new' ? 'Add New Product' : 'Edit Product'}
            </DialogTitle>
          </DialogHeader>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Left: Form */}
            <div className="space-y-3">
              <div>
                <Label className="text-slate-700 font-medium">Product Name *</Label>
                <Input
                  value={form.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  placeholder="e.g. Wireless Earbuds Pro"
                  className="bg-white border-slate-200"
                />
              </div>
              <div>
                <Label className="text-slate-700 font-medium">Image URL *</Label>
                <Input
                  value={form.image}
                  onChange={(e) => handleChange('image', e.target.value)}
                  placeholder="https://..."
                  className="bg-white border-slate-200"
                />
              </div>
              <div>
                <Label className="text-slate-700 font-medium">Category *</Label>
                <select
                  value={form.category}
                  onChange={(e) => handleChange('category', e.target.value)}
                  className="w-full bg-white border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                >
                  <option value="low">Low Margin (Basic Plan)</option>
                  <option value="medium">Medium Margin (Advanced Plan)</option>
                  <option value="high">High Margin (Professional Plan)</option>
                </select>
              </div>
              <div>
                <Label className="text-slate-700 font-medium">Description</Label>
                <textarea
                  value={form.description}
                  onChange={(e) => handleChange('description', e.target.value)}
                  rows={2}
                  placeholder="Short description shown on the card"
                  className="w-full bg-white border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 resize-none"
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-slate-700 font-medium">Product Cost (₹) *</Label>
                  <Input
                    type="number"
                    inputMode="numeric"
                    value={form.productCost}
                    onChange={(e) => handleChange('productCost', e.target.value)}
                    className="bg-white border-slate-200"
                  />
                </div>
                <div>
                  <Label className="text-slate-700 font-medium">Selling Price (₹) *</Label>
                  <Input
                    type="number"
                    inputMode="numeric"
                    value={form.sellingPrice}
                    onChange={(e) => handleChange('sellingPrice', e.target.value)}
                    className="bg-white border-slate-200"
                  />
                </div>
                <div>
                  <Label className="text-slate-700 font-medium">Profit (auto)</Label>
                  <Input
                    type="number"
                    value={form.profit}
                    onChange={(e) => handleChange('profit', e.target.value)}
                    className="bg-slate-50 border-slate-200"
                    readOnly
                  />
                </div>
                <div>
                  <Label className="text-slate-700 font-medium">Margin % (auto)</Label>
                  <Input
                    type="number"
                    value={form.profitMargin}
                    onChange={(e) => handleChange('profitMargin', e.target.value)}
                    className="bg-slate-50 border-slate-200"
                    readOnly
                  />
                </div>
              </div>
              <div>
                <Label className="text-slate-700 font-medium">Avg CPP (e.g. "25-30")</Label>
                <Input
                  value={form.avgCPP}
                  onChange={(e) => handleChange('avgCPP', e.target.value)}
                  className="bg-white border-slate-200"
                />
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <Label className="text-slate-700 font-medium text-xs">Orders @₹1000</Label>
                  <Input
                    value={form.ordersOn1000}
                    onChange={(e) => handleChange('ordersOn1000', e.target.value)}
                    placeholder="e.g. 35+"
                    className="bg-white border-slate-200"
                  />
                </div>
                <div>
                  <Label className="text-slate-700 font-medium text-xs">Orders @₹2000</Label>
                  <Input
                    value={form.ordersOn2000}
                    onChange={(e) => handleChange('ordersOn2000', e.target.value)}
                    placeholder="e.g. 80+"
                    className="bg-white border-slate-200"
                  />
                </div>
                <div>
                  <Label className="text-slate-700 font-medium text-xs">Orders @₹3000</Label>
                  <Input
                    value={form.ordersOn3000}
                    onChange={(e) => handleChange('ordersOn3000', e.target.value)}
                    placeholder="e.g. 150+"
                    className="bg-white border-slate-200"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-slate-700 font-medium">Sort Order</Label>
                  <Input
                    type="number"
                    value={form.sortOrder}
                    onChange={(e) => handleChange('sortOrder', e.target.value)}
                    className="bg-white border-slate-200"
                  />
                </div>
                <div className="flex items-end gap-3 pb-2">
                  <label className="flex items-center gap-1.5 text-sm text-slate-700 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!form.recommended}
                      onChange={(e) => handleChange('recommended', e.target.checked)}
                      className="w-4 h-4 accent-yellow-500"
                    />
                    Recommended
                  </label>
                  <label className="flex items-center gap-1.5 text-sm text-slate-700 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!form.active}
                      onChange={(e) => handleChange('active', e.target.checked)}
                      className="w-4 h-4 accent-blue-500"
                    />
                    Active
                  </label>
                </div>
              </div>
            </div>

            {/* Right: Live Preview */}
            <div>
              <Label className="text-slate-700 font-medium mb-2 block">Live Preview</Label>
              <Card className="bg-white border border-slate-200 overflow-hidden">
                <div className="aspect-square relative bg-slate-50">
                  {form.image ? (
                    <img
                      src={form.image}
                      alt={form.name || 'preview'}
                      className="w-full h-full object-contain p-3"
                      onError={(e) => {
                        e.target.src = 'https://via.placeholder.com/300x300?text=Invalid+URL';
                      }}
                    />
                  ) : (
                    <div className="flex items-center justify-center h-full text-slate-300">
                      <ImageIcon className="w-12 h-12" />
                    </div>
                  )}
                  {form.recommended && (
                    <div className="absolute top-2 left-2 px-2 py-1 bg-gradient-to-r from-yellow-400 to-orange-400 rounded-full flex items-center gap-1 shadow-md">
                      <Star className="w-3 h-3 text-white" fill="white" />
                      <span className="text-[10px] text-white font-bold">RECOMMENDED</span>
                    </div>
                  )}
                </div>
                <CardContent className="p-3">
                  <div className="font-semibold text-slate-800 text-sm line-clamp-2 mb-2 min-h-[2.5rem]">{form.name || 'Product name'}</div>
                  <div className="grid grid-cols-3 gap-1 text-[11px] mb-2">
                    <div className="text-slate-500">Cost<br /><span className="text-slate-800 font-semibold">₹{form.productCost}</span></div>
                    <div className="text-slate-500">Sell<br /><span className="text-slate-800 font-semibold">₹{form.sellingPrice}</span></div>
                    <div className="text-slate-500">Profit<br /><span className="text-green-600 font-semibold">₹{form.profit}</span></div>
                  </div>
                  <div className="flex items-center justify-between text-[11px] text-slate-500">
                    <span className="flex items-center gap-1"><TrendingUp className="w-3 h-3 text-green-600" />{Number(form.profitMargin || 0).toFixed(1)}%</span>
                    <span className="text-blue-600">CPP: ₹{form.avgCPP || '-'}</span>
                  </div>
                </CardContent>
              </Card>
              <p className="text-[11px] text-slate-400 mt-2 italic">
                This is exactly how the card will appear during user onboarding.
              </p>
            </div>
          </div>

          <DialogFooter className="gap-2 mt-4">
            <Button variant="outline" onClick={closeEdit} className="border-slate-200">
              <X className="w-4 h-4 mr-1" /> Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white">
              <Save className="w-4 h-4 mr-1" /> {saving ? 'Saving...' : editing === 'new' ? 'Add Product' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProductsCatalogManagement;
